import { Trans } from "@lingui/react";
import { useScreenSize, WIcon } from "@mobility-item-page/client";
import type { ReactNode } from "react";
import { ObjectPosition } from "../../types/objectPosition.ts";
import { DealerRatings } from "../components/DealerRatings.tsx";
import { Description } from "../components/Description.tsx";
import { ErrorInfo } from "../components/ErrorInfo.tsx";
import { DealerBrandingPageButton, HomepageLink, OtherAdsLink } from "../components/Links.tsx";
import { LocationSection } from "../components/LocationSection.tsx";
import { Logo } from "../components/Logo.tsx";
import { NotFound } from "../components/NotFound.tsx";
import { OpeningHours } from "../components/OpeningHours.tsx";
import { ScreenReaderSeparator } from "../components/ScreenReaderSeparator.tsx";
import { useValidDealerItemContext } from "../context/DealerItemProviderContext.tsx";
import { useTracking } from "../utils/pulseHelper.ts";

export const DealerOverview = () => {
  const {
    organisationData: { openingHoursInfo, location },
    itemData: { location: itemLocation },
  } = useValidDealerItemContext();
  const { trackViewOpeningHours, trackSeeLocation } = useTracking();

  return (
    <Container>
      <div className="py-40 grid gap-40 md:grid-cols-[2fr_1fr] md:items-start">
        <div className="flex flex-col gap-40">
          <Info />
          {location && (
            <LocationSection
              trackClick={(elementType, linkUrl) => trackSeeLocation(ObjectPosition.Overview, elementType, linkUrl)}
              location={location}
              showMap={!itemLocation}
            />
          )}
          <OpeningHours openingHoursInfo={openingHoursInfo} trackClick={trackViewOpeningHours} />
        </div>
        <Sidebar />
        <OrgNumber />
      </div>
    </Container>
  );
};

const Container = ({ children }: { children: ReactNode }) => {
  const { isMobile } = useScreenSize();
  return (
    // -mt-1 is used to make the container cover the bottom border of the previous section
    // mx-[-16px] are used to ensure the background color extends to the edges of the viewport when banner ads with too low z-index are rendered
    <div
      className={`relative mt-40 px-16 sm:px-24 s-bg-info-subtle dark:s-bg-subtle ${isMobile ? "mx-[-16px]" : "rounded-8"}`}
    >
      {children}
    </div>
  );
};

const Info = () => {
  const {
    organisationData: { name, description },
  } = useValidDealerItemContext();
  const { trackViewFullDescription, trackDealerRatings } = useTracking();

  return (
    <div>
      <Logo className="mb-16" />
      <h2 className="t3 mt-16 mb-4">{name}</h2>
      <DealerRatings trackClick={(url) => trackDealerRatings(url, ObjectPosition.Overview)} className="mb-4" />
      <Description className="mt-12 mb-32" trackClick={trackViewFullDescription} description={description} />
      <DealerBrandingPageButton />
    </div>
  );
};

const Sidebar = () => {
  return (
    <div className="flex flex-col gap-40">
      <Links className="order-3 md:order-1" />
      <AuthorizedBrands className="order-1 md:order-2" />
      <Offerings className="order-2 md:order-3" />
    </div>
  );
};

const Links = ({ className = "" }: { className?: string }) => {
  const { trackSeeOtherAdsClick, trackHomepage } = useTracking();
  const {
    organisationData: { homePage },
    external: { homepageSurvey },
  } = useValidDealerItemContext();
  return (
    <div className={`${className}`}>
      <h4 className="mb-16">
        <Trans id="dealer.dealerOverview.links" message="Links" />
      </h4>
      <ul className="flex flex-col gap-12">
        <li>
          <OtherAdsLink trackClick={() => trackSeeOtherAdsClick(ObjectPosition.Overview)} />
        </li>
        {homePage && (
          <li>
            <HomepageLink
              homePage={homePage}
              trackClick={() => trackHomepage(ObjectPosition.Overview)}
              homepageSurvey={homepageSurvey}
            />
          </li>
        )}
      </ul>
    </div>
  );
};

const AuthorizedBrands = ({ className = "" }: { className?: string }) => {
  const {
    organisationData: { authorizedBrandDealer },
  } = useValidDealerItemContext();

  const brands = authorizedBrandDealer ? Object.values(authorizedBrandDealer).flat() : [];

  if (brands.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <h4 className="mb-16">
        <Trans id="dealer.dealerOverview.authorizedBrands" message="Authorized dealer of" />
      </h4>
      <ul className="flex flex-wrap gap-12">
        {brands.map((brand) => (
          <li key={brand} className="flex gap-8">
            <WIcon name="Warranty" className="shrink-0 mt-2 s-icon" />
            <ScreenReaderSeparator />
            <span>
              <b>{brand}</b>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Offerings = ({ className = "" }: { className?: string }) => {
  const {
    organisationData: { uniqueSellingPoints },
  } = useValidDealerItemContext();

  if (!uniqueSellingPoints || uniqueSellingPoints.length === 0) {
    return null;
  }
  return (
    <div className={className}>
      <h4 className="mb-16">
        <Trans id="dealer.dealerOverview.offerings" message="We offer" />
      </h4>
      <ul className="flex flex-col gap-12">
        {uniqueSellingPoints.map((sellingPoint) => (
          <li key={sellingPoint} className="flex gap-8">
            <WIcon name="Check" className="shrink-0 mt-2 s-icon" />
            <ScreenReaderSeparator />
            <span>{sellingPoint}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const OrgNumber = () => {
  const {
    organisationData: { companyRegistration, dsaAcceptedText },
  } = useValidDealerItemContext();
  const { trackCompanyRegistration } = useTracking();

  if (!companyRegistration) {
    return null;
  }

  const { registrationNumber, registrationNumberShortLinkTitle, registrationNumberLinkUrl } = companyRegistration;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-16">
        {/* WebKit's aggressive phone number detection changes text into a link so this is an attempt to prevent it */}
        <span className="s-text! no-underline!">{registrationNumber}</span>
        {registrationNumberLinkUrl && (
          <>
            <span>·</span>
            <a
              className="flex items-center gap-4"
              href={registrationNumberLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCompanyRegistration(ObjectPosition.Overview)}
            >
              {registrationNumberShortLinkTitle}
              <ScreenReaderSeparator />
              <WIcon name="LinkExternal" />
            </a>
          </>
        )}
      </div>
      {dsaAcceptedText && <p className="text-caption s-text-subtle mt-8">{dsaAcceptedText}</p>}
    </div>
  );
};

export const DealerOverviewFallback = () => {
  return (
    <Container>
      <div className="py-56">
        <h2 className="mb-16">
          <Trans id="dealer.dealerOverview.fallbackTitle" message="Dealers profile" />
        </h2>
        <div className="border rounded-8 flex gap-16 items-center p-16 s-bg">
          <NotFound />
          <div className="">
            <ErrorInfo />
          </div>
        </div>
      </div>
    </Container>
  );
};
